import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BookingGridBase from '../common/BookingGridBase';
import ResourceLegend from '../common/ResourceLegend';
import BookingCardOrderedResources from '../common/BookingCardOrderedResources';
import InternalBookingCardBase from '../common/InternalBookingCardBase';
import useResource from '../../../../hooks/useResource';
import { toast } from 'react-toastify';
import { generateUniqueColor } from '../../../../util/colorUtil';
import { formatDurationCompact } from '../../../../util/formatHelper';
import { computeBookingDurationMs } from '../../../../util/bookingGridUtil';
import { formatOrderedResourcesByGroup } from '../../../../util/orderedResourcesDisplay';
import { getCapacityParticipantCount } from './capacityOverride';

const normalizeToIntervalStart = (date, intervalMs) => {
  if (!(date instanceof Date) || !Number.isFinite(intervalMs) || intervalMs <= 0) {
    return new Date(date);
  }
  const normalized = Math.floor(date.getTime() / intervalMs) * intervalMs;
  return new Date(normalized);
};

const resolveBlockedSlotCount = booking => {
  const raw = booking?.blockedSlots ?? booking?.laneCount ?? booking?.participantCount ?? 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.max(1, Math.floor(n));
};

const sortResourceIdsByDisplayName = (resourceIds, nameById) => {
  const ids = Array.isArray(resourceIds) ? resourceIds.slice() : [];
  return ids.sort((a, b) => {
    const aName = nameById instanceof Map ? nameById.get(String(a)) || String(a) : String(a);
    const bName = nameById instanceof Map ? nameById.get(String(b)) || String(b) : String(b);
    const byName = String(aName).localeCompare(String(bName), 'de', { numeric: true, sensitivity: 'base' });
    if (byName !== 0) {
      return byName;
    }
    return String(a).localeCompare(String(b));
  });
};

const NearestBookingsHeader = ({
  bookings,
  orderedResourcesConfig,
  hoveredBookingId,
  handleOpenModal,
  handleHoverStart,
  handleHoverEnd,
}) => {
  const theme = useTheme();
  const entries = useMemo(() => {
    if (!Array.isArray(bookings) || bookings.length === 0) {
      return [];
    }
    return bookings.map(bk => ({
      booking: bk,
      durationMs: computeBookingDurationMs(bk, orderedResourcesConfig),
      color: generateUniqueColor(String(bk._id)),
    }));
  }, [bookings, orderedResourcesConfig]);

  if (entries.length === 0) {
    return null;
  }

  return entries.map(({ booking, durationMs, color }) => {
    const isHighlighted = hoveredBookingId === String(booking._id);
    const orderedLine = formatOrderedResourcesByGroup(booking, orderedResourcesConfig?.nameById);
    return (
      <div
        key={`detail-${booking._id}`}
        style={{
          border: `2px solid ${color}`,
          borderRadius: '8px',
          padding: '8px 12px',
          background: isHighlighted
            ? alpha(color, 0.25)
            : theme.palette.mode === 'dark'
              ? theme.palette.grey[900]
              : theme.palette.grey[100],
          cursor: 'pointer',
        }}
        onClick={() => handleOpenModal?.(booking._id)}
        onMouseEnter={() => handleHoverStart(booking)}
        onMouseLeave={handleHoverEnd}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700 }}>
            {booking.firstName} {booking.lastName}
          </div>
          <div
            style={{
              minWidth: 28,
              height: 28,
              borderRadius: 14,
              background:
                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[400],
              color: theme.palette.getContrastText(
                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[400]
              ),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              marginLeft: 8,
            }}
            title='Teilnehmer'
          >
            {booking.participantCount}
          </div>
        </div>
        <div style={{ opacity: 0.9, marginTop: 4 }}>
          {new Date(booking.startTime).toLocaleTimeString().substring(0, 5)}
          {` | ${formatDurationCompact(durationMs)}`}
        </div>
        {orderedLine ? (
          <div style={{ opacity: 0.85, marginTop: 2 }}>{orderedLine}</div>
        ) : booking.pricingName ? (
          <div style={{ opacity: 0.85, marginTop: 2 }}>{booking.pricingName}</div>
        ) : null}
      </div>
    );
  });
};

/**
 * Generic ordered-resources grid used by multiple entertainments (e.g., Bash, Golf).
 * Fetches ordered resource configuration, renders lanes and booking segments consistently.
 */
function GridOrderedResources({
  bookings,
  rowCount,
  date,
  updateBooking,
  handleOpenModal,
  entertainmentId,
  capacity,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  draggedBookingDuration,
  isShiftable,
  isLoadingShiftTimes,
  isBookingReadyForShift,
  useVerticalForInternal = false,
  VerticalComponent,
  showLoadingLegend = false,
  resourceType = undefined,
  useResponsiveNearestHeader = false,
  showAllSlots = false,
  onIntervalModeChange = () => {},
}) {
  const theme = useTheme();
  const [baseOrderedResourcesConfig, setBaseOrderedResourcesConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getResourcesByEntertainmentId } = useResource();
  const isMdUp = useMediaQuery('(min-width: 1200px)');
  const isSmUp = useMediaQuery('(min-width: 768px)');
  const cols = isMdUp ? 4 : isSmUp ? 2 : 1;

  useEffect(() => {
    const fetch = async () => {
      if (!entertainmentId) return;
      try {
        setLoading(true);
        // ask backend for resources and orderedResourceIds together
        const resp = await getResourcesByEntertainmentId(entertainmentId, true);
        const allResources = Array.isArray(resp) ? resp : resp?.resources || [];
        const orderedIds = Array.isArray(resp?.orderedResourceIds) ? resp.orderedResourceIds : [];
        const durationsMapObj = resp?.orderedResourceDurationsMsById || null;
        const intervalMetaRaw = resp?.orderedResourcesInterval || null;

        const idToName = new Map((allResources || []).map(r => [r._id.toString(), r.name]));
        const maxPersonsById = new Map(
          (allResources || []).map(r => [
            r?._id?.toString?.() || String(r?._id),
            Number.isFinite(Number(r?.maxPersons)) && Number(r.maxPersons) > 0 ? Number(r.maxPersons) : null,
          ])
        );

        const durationByIdMs = new Map();
        if (durationsMapObj && typeof durationsMapObj === 'object') {
          Object.entries(durationsMapObj).forEach(([rid, ms]) => {
            const v = Number(ms) || 0;
            if (v > 0) durationByIdMs.set(String(rid), v);
          });
        }

        const startIntervalMinutes = Number(intervalMetaRaw?.startIntervalMinutes || 0);
        const intervalMs = startIntervalMinutes > 0 ? startIntervalMinutes * 60 * 1000 : null;
        const intervalPersonCapacity = Number(intervalMetaRaw?.intervalPersonCapacity || 0) || null;
        const maxBookingGroups = Number(intervalMetaRaw?.maxBookingGroups || 0) || null;
        const startAtAnyPosition = Boolean(intervalMetaRaw?.startAtAnyPosition);
        const isIntervalMode = Boolean(intervalMs && intervalMs > 0);

        const order = [];
        const baseSlotCountById = new Map();

        const registerResource = rid => {
          const ridStr = String(rid);
          const name = idToName.get(ridStr) || ridStr.substring(0, 6);
          const durationMs = Number(durationByIdMs.get(ridStr) || 0);
          order.push(ridStr);
          const baseCount =
            isIntervalMode && intervalMs
              ? Math.max(1, Math.ceil((durationMs || 0) / intervalMs) || 1)
              : 1;
          baseSlotCountById.set(ridStr, baseCount);
          return { ridStr, name };
        };

        if (orderedIds.length > 0) {
          orderedIds.forEach(registerResource);
        } else {
          (allResources || []).forEach(r => registerResource(r._id.toString()));
        }

        const shouldUseAlphabeticRoomOrder = resourceType === 'bash';
        if (shouldUseAlphabeticRoomOrder) {
          const sorted = sortResourceIdsByDisplayName(order, idToName);
          order.length = 0;
          order.push(...sorted);
        }

        setBaseOrderedResourcesConfig({
          durationByIdMs,
          order,
          nameById: idToName,
          maxPersonsById,
          intervalMs,
          isIntervalMode,
          intervalMeta: {
            startIntervalMinutes,
            intervalPersonCapacity,
            maxBookingGroups,
            startAtAnyPosition,
            // Number of configured courses/resources for this entertainment
            resourceCount: Math.max(1, order.length),
          },
          baseSlotCountById,
        });
      } catch (e) {
        console.error(e);
        toast.error('Fehler beim Laden der Ordered-Resources');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [entertainmentId]);

  useEffect(() => {
    onIntervalModeChange?.(Boolean(baseOrderedResourcesConfig?.isIntervalMode));
  }, [baseOrderedResourcesConfig?.isIntervalMode, onIntervalModeChange]);

  const { orderedResourcesConfig, laneLabelEntries, segmentStackIndexByKey } = useMemo(() => {
    if (!baseOrderedResourcesConfig) {
      return {
        orderedResourcesConfig: null,
        laneLabelEntries: [],
        segmentStackIndexByKey: new Map(),
      };
    }

    const {
      order,
      nameById,
      durationByIdMs,
      intervalMs,
      isIntervalMode,
      intervalMeta,
      baseSlotCountById,
    } = baseOrderedResourcesConfig;

    const slotMs = Number(intervalMs || 0);
    const baseCountById = baseSlotCountById instanceof Map ? baseSlotCountById : new Map();

    const groupToBookingIds = new Map(); // `${rid}:${slotISO}` -> Set<bookingId>
    (Array.isArray(bookings) ? bookings : []).forEach(bk => {
      if (!bk || bk.status === 'blocked') return;
      const bookingId = bk?._id?.toString?.() || String(bk._id);
      const baseStart = new Date(bk.startTime);
      if (Number.isNaN(baseStart.getTime())) return;
      const segs = Array.isArray(bk.orderedResourceIds) ? bk.orderedResourceIds : [];

      segs.forEach(seg => {
        const rid = seg?.resourceId?._id || seg?.resourceId;
        if (!rid) return;
        if (!isIntervalMode || !(slotMs > 0)) return;
        const offMin = Number(seg?.startOffsetMinutes || 0);
        if (!Number.isFinite(offMin)) return;
        const segStart = new Date(baseStart.getTime() + offMin * 60000);
        const slotStart = normalizeToIntervalStart(segStart, slotMs);
        const slotISO = slotStart.toISOString();
        const key = `${String(rid)}:${slotISO}`;
        if (!groupToBookingIds.has(key)) groupToBookingIds.set(key, new Set());
        groupToBookingIds.get(key).add(bookingId);
      });
    });

    const maxConcurrencyById = new Map();
    for (const [key, set] of groupToBookingIds.entries()) {
      const [rid] = key.split(':');
      const count = set.size;
      const prev = maxConcurrencyById.get(rid) || 1;
      if (count > prev) maxConcurrencyById.set(rid, count);
    }

    const laneIndexById = new Map();
    const laneCountById = new Map();
    const laneOffsetsById = new Map();
    const computedLaneLabelEntries = [];

    let laneCursor = 1;
    order.forEach(rid => {
      const baseSlots = Math.max(1, Number(baseCountById.get(rid) || 1));
      const concurrency =
        isIntervalMode && slotMs > 0 ? Math.max(1, Number(maxConcurrencyById.get(rid) || 1)) : 1;
      const totalLanes = baseSlots * concurrency;

      laneIndexById.set(rid, laneCursor);
      laneCountById.set(rid, totalLanes);
      laneOffsetsById.set(
        rid,
        Array.from({ length: totalLanes }, (_, idx) => idx)
      );

      const name = nameById instanceof Map ? nameById.get(rid) : null;
      const label = name || rid.substring(0, 6);
      for (let laneOffset = 0; laneOffset < totalLanes; laneOffset += 1) {
        const displayName = label;
        computedLaneLabelEntries.push({
          _id: `${rid}::${laneOffset}`,
          name: displayName,
          resourceId: rid,
          laneIndex: laneCursor + laneOffset,
          laneOffset,
        });
      }
      laneCursor += totalLanes;
    });

    const stackIndexMap = new Map(); // `${bookingId}:${rid}:${slotISO}` -> stackIndex
    for (const [groupKey, set] of groupToBookingIds.entries()) {
      const [rid, ...rest] = groupKey.split(':');
      const slotISO = rest.join(':');
      const bookingIds = Array.from(set.values()).sort();
      bookingIds.forEach((bid, idx) => {
        stackIndexMap.set(`${bid}:${rid}:${slotISO}`, idx);
      });
    }

    return {
      orderedResourcesConfig: {
        laneIndexById,
        laneCountById,
        durationByIdMs,
        order,
        nameById,
        intervalMs,
        isIntervalMode,
        intervalMeta,
        laneOffsetsById,
        totalLaneCount: computedLaneLabelEntries.length,
        baseSlotCountById: baseCountById,
      },
      laneLabelEntries: computedLaneLabelEntries,
      segmentStackIndexByKey: stackIndexMap,
    };
  }, [baseOrderedResourcesConfig, bookings]);

  const isReady = useMemo(() => {
    return !!orderedResourcesConfig && orderedResourcesConfig?.laneIndexById?.size > 0 && !loading;
  }, [orderedResourcesConfig, loading]);

  const [hoveredBookingId, setHoveredBookingId] = useState(null);
  const [dragPreviewStart, setDragPreviewStart] = useState(null);
  const [activeDragBookingId, setActiveDragBookingId] = useState(null);

  const handleHoverStart = bk => {
    if (activeDragBookingId && String(activeDragBookingId) !== String(bk._id)) return;
    setHoveredBookingId(String(bk._id));
  };
  const handleHoverEnd = () => setHoveredBookingId(null);

  // Determine effective dragged duration in minutes (ordered-resources aware)
  const effectiveDraggedMinutes = useMemo(() => {
    try {
      if (!activeDragBookingId) return draggedBookingDuration;
      const bk = Array.isArray(bookings)
        ? bookings.find(b => String(b._id) === String(activeDragBookingId))
        : null;
      if (!bk) return draggedBookingDuration;
      const list = Array.isArray(bk.orderedResourceIds) ? bk.orderedResourceIds : [];
      if (!orderedResourcesConfig?.durationByIdMs || list.length === 0) {
        return draggedBookingDuration;
      }
      let sumMs = 0;
      let missing = false;
      list.forEach(x => {
        const rid = x.resourceId?._id || x.resourceId;
        const ms = orderedResourcesConfig.durationByIdMs.get(String(rid)) || 0;
        if (!ms) missing = true;
        sumMs += ms;
      });
      if (sumMs > 0 && !missing) return Math.round(sumMs / 60000);
      return draggedBookingDuration;
    } catch {
      return draggedBookingDuration;
    }
  }, [activeDragBookingId, bookings, orderedResourcesConfig, draggedBookingDuration]);

  const laneLabelEntryMap = useMemo(() => {
    const map = new Map();
    laneLabelEntries.forEach(entry => {
      map.set(`${entry.resourceId}::${entry.laneOffset}`, entry);
    });
    return map;
  }, [laneLabelEntries]);

  const activeLaneIndices = useMemo(() => {
    if (!orderedResourcesConfig) {
      return new Set();
    }
    const indices = new Set();
    const { laneIndexById, laneCountById, isIntervalMode, intervalMs, order, baseSlotCountById } =
      orderedResourcesConfig;

    (Array.isArray(bookings) ? bookings : []).forEach(booking => {
      if (!booking) return;

      if (booking.status === 'blocked') {
        const ridRaw = booking?.resourceId?._id || booking?.resourceId;
        if (ridRaw) {
          const rid = String(ridRaw);
          const baseLane = laneIndexById.get(rid);
          const laneCount = laneCountById.get(rid) || 1;
          if (baseLane != null) {
            const slotsToBlock = Math.min(laneCount, resolveBlockedSlotCount(booking));
            for (let offset = 0; offset < slotsToBlock; offset += 1) {
              indices.add(baseLane + offset);
            }
          }
          return;
        }
        order.forEach(resId => {
          const baseLane = laneIndexById.get(resId);
          const laneCount = laneCountById.get(resId) || 1;
          if (baseLane != null) {
            for (let offset = 0; offset < laneCount; offset += 1) {
              indices.add(baseLane + offset);
            }
          }
        });
        return;
      }

      const orderedResourceIds = Array.isArray(booking.orderedResourceIds)
        ? booking.orderedResourceIds
        : [];
      if (orderedResourceIds.length === 0) return;

      const baseStartDate = new Date(booking.startTime);
      if (Number.isNaN(baseStartDate.getTime())) return;

      orderedResourceIds.forEach(segment => {
        const rid = segment?.resourceId?._id || segment?.resourceId;
        if (!rid) return;
        const ridStr = String(rid);
        const baseLane = laneIndexById.get(ridStr);
        if (baseLane == null) return;

        const laneCount = laneCountById.get(ridStr) || 1;
        const startOffsetMinutes = Number(segment?.startOffsetMinutes || 0);
        const segStartDate = new Date(baseStartDate.getTime() + startOffsetMinutes * 60000);

        let laneOffset = 0;
        if (isIntervalMode && laneCount > 1 && Number.isFinite(intervalMs) && intervalMs > 0) {
          const slotStart = normalizeToIntervalStart(segStartDate, intervalMs);
          const slotISO = slotStart.toISOString();
          const baseSlots =
            baseSlotCountById instanceof Map
              ? Math.max(1, Number(baseSlotCountById.get(ridStr) || 1))
              : 1;
          const slotIndex = Math.floor(slotStart.getTime() / intervalMs);
          const timeOffset = ((slotIndex % baseSlots) + baseSlots) % baseSlots;
          const bookingId = booking?._id?.toString?.() || String(booking._id);
          const stackKey = `${bookingId}:${ridStr}:${slotISO}`;
          const stackIndex = Number(segmentStackIndexByKey.get(stackKey) || 0);
          const safeStack =
            Number.isFinite(stackIndex) && stackIndex >= 0 ? Math.floor(stackIndex) : 0;
          laneOffset = timeOffset + safeStack * baseSlots;
        } else if (isIntervalMode && laneCount > 1) {
          const groupIndex = Number(segment?.groupIndex || 1);
          laneOffset = (((groupIndex - 1) % laneCount) + laneCount) % laneCount;
        }

        indices.add(baseLane + laneOffset);
      });
    });

    return indices;
  }, [bookings, orderedResourcesConfig, segmentStackIndexByKey]);

  const { visibleLaneEntries, laneOffsetDisplayIndexById } = useMemo(() => {
    if (!orderedResourcesConfig) {
      return { visibleLaneEntries: [], laneOffsetDisplayIndexById: new Map() };
    }
    const shouldFilter = Boolean(orderedResourcesConfig.isIntervalMode) && !showAllSlots;
    const resultEntries = [];
    const mapping = new Map();
    let laneCursor = 1;

    orderedResourcesConfig.order.forEach(rid => {
      const baseLane = orderedResourcesConfig.laneIndexById.get(rid) || 0;
      const laneCount = orderedResourcesConfig.laneCountById.get(rid) || 1;
      const offsets = Array.from({ length: laneCount }, (_, idx) => idx);
      const offsetsToDisplay = shouldFilter
        ? offsets.filter(offset => activeLaneIndices.has(baseLane + offset))
        : offsets;
      if (offsetsToDisplay.length === 0) {
        return;
      }

      const offsetMap = new Map();
      offsetsToDisplay.forEach(offset => {
        const key = `${rid}::${offset}`;
        const entry = laneLabelEntryMap.get(key);
        if (!entry) {
          return;
        }
        const mappedIndex = laneCursor;
        resultEntries.push({
          ...entry,
          laneIndex: mappedIndex,
        });
        offsetMap.set(offset, mappedIndex);
        laneCursor += 1;
      });

      mapping.set(rid, offsetMap);
    });

    return { visibleLaneEntries: resultEntries, laneOffsetDisplayIndexById: mapping };
  }, [orderedResourcesConfig, laneLabelEntryMap, showAllSlots, activeLaneIndices]);

  const stepMinutes = useMemo(() => {
    const isGolfInterval =
      resourceType === 'golf' &&
      Boolean(orderedResourcesConfig?.isIntervalMode) &&
      Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0) > 0;
    if (!isGolfInterval) return 30;
    return Math.max(1, Number(orderedResourcesConfig?.intervalMeta?.startIntervalMinutes || 0) || 30);
  }, [resourceType, orderedResourcesConfig]);

  const calcAttendance = useMemo(() => {
    return () => {
      const intervalPersonCapacity = Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0);
      const resourceCount = Math.max(1, Number(orderedResourcesConfig?.intervalMeta?.resourceCount || 0) || 1);
      const isGolfInterval =
        resourceType === 'golf' &&
        Boolean(orderedResourcesConfig?.isIntervalMode) &&
        intervalPersonCapacity > 0;

      const cap = isGolfInterval
        ? intervalPersonCapacity * resourceCount
        : visibleLaneEntries.length || 0;

      const stepMinutes = isGolfInterval
        ? Math.max(
            1,
            Number(orderedResourcesConfig?.intervalMeta?.startIntervalMinutes || 0) || 30
          )
        : 30;

      const times = Array.from({ length: Math.floor((24 - 9) * 60 / stepMinutes) + 1 }, (_, index) => {
        const totalMin = index * stepMinutes;
        const hour = 9 + Math.floor(totalMin / 60);
        const minute = totalMin % 60;
        const minuteLabel = String(minute).padStart(2, '0');
        const qualifiedTime = new Date(
          date.get('year'),
          date.get('month'),
          date.get('date'),
          hour,
          minute
        );
        return {
          isFull: minute === 0,
          time: `${hour}:${minuteLabel}`,
          qualifiedTime,
          i: index,
          s0: 0,
          frees0: cap,
        };
      });

      if (!Array.isArray(bookings) || bookings.length === 0 || cap <= 0) {
        return times;
      }

      const visibleCountByResourceId = (() => {
        const map = new Map();
        visibleLaneEntries.forEach(entry => {
          const rid = entry?.resourceId ? String(entry.resourceId) : null;
          if (!rid) return;
          map.set(rid, (map.get(rid) || 0) + 1);
        });
        return map;
      })();

      const durationByIdMs =
        orderedResourcesConfig?.durationByIdMs instanceof Map ? orderedResourcesConfig.durationByIdMs : null;

      for (const bk of bookings) {
        if (!bk) continue;

        // Blockers: occupy either a specific resource lane-group or the full grid.
        if (bk?.status === 'blocked') {
          const bStart = new Date(bk?.startTime);
          const bEnd = new Date(bk?.endTime);
          if (Number.isNaN(bStart.getTime()) || Number.isNaN(bEnd.getTime())) continue;

          const ridRaw = bk?.resourceId?._id || bk?.resourceId || null;
          const rid = ridRaw ? String(ridRaw) : null;
          const requested = resolveBlockedSlotCount(bk);
          const lanesToBlock = rid
            ? Math.min(visibleCountByResourceId.get(rid) || 0, requested)
            : cap;
          if (lanesToBlock <= 0) continue;

          for (const t of times) {
            if (bStart <= t.qualifiedTime && bEnd > t.qualifiedTime) {
              t.frees0 -= lanesToBlock;
            }
          }
          continue;
        }

        // Bookings: sum segment usage across ordered resources.
        if (!durationByIdMs) continue;

        const bookingType = bk?.bookingType;
        const baseStartKey = bookingType === 'RegiondoBooking' ? 'eventStart' : 'startTime';
        const baseStart = new Date(bk?.[baseStartKey]);
        if (Number.isNaN(baseStart.getTime())) continue;

        const segments = Array.isArray(bk.orderedResourceIds) ? bk.orderedResourceIds : [];
        if (segments.length === 0) continue;

        let fallbackParticipantsPerGroup = 0;
        if (isGolfInterval) {
          const uniqueGroups = new Set(
            segments
              .map(s => Number(s?.groupIndex || 1))
              .map(v => (Number.isFinite(v) && v > 0 ? v : 1))
          );
          const groupCount = Math.max(1, uniqueGroups.size);
          const total = getCapacityParticipantCount(bk);
          fallbackParticipantsPerGroup = total > 0 ? Math.ceil(total / groupCount) : 0;
        }

        if (isGolfInterval) {
          // For interval-mode Golf: capacity is consumed only at each group's start time,
          // not for every room segment the group passes through.
          const groupMap = new Map();
          for (const seg of segments) {
            const groupIdxRaw = Number(seg?.groupIndex);
            const groupIdx = Number.isFinite(groupIdxRaw) && groupIdxRaw > 0 ? groupIdxRaw : 1;
            const offsetRaw = Number(seg?.startOffsetMinutes ?? 0);
            const offset = Number.isFinite(offsetRaw) ? offsetRaw : 0;
            const participants = Number(seg?.participantCount || 0) || fallbackParticipantsPerGroup || 0;

            const existing = groupMap.get(groupIdx);
            if (!existing) {
              groupMap.set(groupIdx, { minOffset: offset, participants });
            } else {
              if (offset < existing.minOffset) existing.minOffset = offset;
              if (existing.participants <= 0 && participants > 0) existing.participants = participants;
            }
          }

          for (const { minOffset, participants } of groupMap.values()) {
            if (!(participants > 0)) continue;
            const groupStart = new Date(baseStart.getTime() + minOffset * 60 * 1000);
            // Use interval step as slot duration so occupancy falls in exactly one interval bucket
            const intervalMs = Number(orderedResourcesConfig?.intervalMs || 0);
            const slotDuration = intervalMs > 0 ? intervalMs : 30 * 60 * 1000;
            const groupEnd = new Date(groupStart.getTime() + slotDuration);

            for (const t of times) {
              if (groupStart <= t.qualifiedTime && groupEnd > t.qualifiedTime) {
                t.s0 += participants;
                t.frees0 -= participants;
              }
            }
          }
          continue;
        }

        for (const seg of segments) {
          const ridRaw = seg?.resourceId?._id || seg?.resourceId || null;
          const rid = ridRaw ? String(ridRaw) : null;
          if (!rid) continue;

          const durationMs = Number(durationByIdMs.get(rid) || 0);
          if (!Number.isFinite(durationMs) || durationMs <= 0) continue;

          const offsetMin = Number(seg?.startOffsetMinutes || 0);
          if (!Number.isFinite(offsetMin)) continue;

          const segStart = new Date(baseStart.getTime() + offsetMin * 60 * 1000);
          const segEnd = new Date(segStart.getTime() + durationMs);

          for (const t of times) {
            if (segStart <= t.qualifiedTime && segEnd > t.qualifiedTime) {
              t.s0 += 1;
              t.frees0 -= 1;
            }
          }
        }
      }

      return times;
    };
  }, [bookings, date, visibleLaneEntries, orderedResourcesConfig, resourceType]);

  const hasRegiondoBookings = Array.isArray(bookings)
    ? bookings.some(b => b?.bookingType !== 'InternalBooking')
    : false;
  const shouldRenderVertical = hasRegiondoBookings || !useVerticalForInternal;

  if (shouldRenderVertical && VerticalComponent) {
    const intervalPersonCapacity = Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0);
    const resourceCount = Math.max(1, Number(orderedResourcesConfig?.intervalMeta?.resourceCount || 0) || 1);
    const isGolfIntervalCapacity =
      resourceType === 'golf' &&
      orderedResourcesConfig?.isIntervalMode &&
      intervalPersonCapacity > 0;

    const capacityTimes = (() => {
      try {
        return calcAttendance?.() || null;
      } catch (e) {
        return null;
      }
    })();

    const isOrderedResourcesCapacity =
      !orderedResourcesConfig?.isIntervalMode &&
      orderedResourcesConfig?.order?.length > 0;

    const verticalSlotCapacity =
      isGolfIntervalCapacity || isOrderedResourcesCapacity
        ? isGolfIntervalCapacity
          ? intervalPersonCapacity * resourceCount
          : resourceCount
        : Number(visibleLaneEntries?.length || 0);

    const verticalCapacityData =
      isGolfIntervalCapacity
        ? {
            entertainmentName: capacity?.entertainmentName || entertainment?.label || '',
            usesResources: true,
            capacity: intervalPersonCapacity * resourceCount,
            totalBookings: Array.isArray(bookings) ? bookings.length : 0,
          }
        : isOrderedResourcesCapacity
          ? {
              entertainmentName: capacity?.entertainmentName || entertainment?.label || '',
              usesResources: true,
              capacity: resourceCount,
              totalBookings: Array.isArray(bookings) ? bookings.length : 0,
            }
          : capacity;
    return (
      <VerticalComponent
        bookings={bookings}
        date={date}
        slotCapacity={verticalSlotCapacity}
        updateBooking={updateBooking}
        capacity={verticalCapacityData}
        capacityTimes={capacityTimes}
        handleOpenModal={handleOpenModal}
        orderedResourcesConfig={orderedResourcesConfig}
      />
    );
  }

  if (showLoadingLegend && !isReady) {
    return (
      <BookingGridBase
        bookings={[]}
        rowCount={1}
        date={date}
        slotCapacity={0}
        calcAttendance={calcAttendance}
        capacityData={capacity}
        availableShiftTimes={availableShiftTimes}
        draggedBookingDuration={draggedBookingDuration}
        isLoadingShiftTimes={isLoadingShiftTimes}
        renderLegend={() => <ResourceLegend resources={[]} loading={true} />}
        renderAvailableSlots={false}
        renderBookingCard={() => null}
      />
    );
  }

  const baseProps = {
    bookings,
    rowCount: Math.max(1, visibleLaneEntries.length),
    date,
    standaloneCapacityBar: Boolean(orderedResourcesConfig?.isIntervalMode),
    slotCapacity:
      resourceType === 'golf' &&
      orderedResourcesConfig?.isIntervalMode &&
      Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0) > 0
        ? Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0) *
          Math.max(1, Number(orderedResourcesConfig?.intervalMeta?.resourceCount || 0) || 1)
        : visibleLaneEntries.length,
    calcAttendance,
    capacityData:
      resourceType === 'golf' &&
      orderedResourcesConfig?.isIntervalMode &&
      Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0) > 0
        ? {
            entertainmentName: capacity?.entertainmentName || entertainment?.label || '',
            usesResources: true,
            capacity:
              Number(orderedResourcesConfig?.intervalMeta?.intervalPersonCapacity || 0) *
              Math.max(1, Number(orderedResourcesConfig?.intervalMeta?.resourceCount || 0) || 1),
            totalBookings: Array.isArray(bookings) ? bookings.length : 0,
          }
        : capacity,
    availableShiftTimes,
    draggedBookingDuration: effectiveDraggedMinutes,
    isLoadingShiftTimes,
    slotWidthPx: 80,
    minutesPerSlot: stepMinutes,
    laneLabelWidth: 140,
    renderLaneLabel: ({ index }) => {
      const res = visibleLaneEntries[index - 1];
      if (!res) return null;
      return (
        <div
          title={res.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: theme.palette.text.secondary,
            paddingRight: 12,
          }}
        >
          {res.name}
        </div>
      );
    },
    renderHeaderExtras: ({ bookings: nearest }) => (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: '12px',
        }}
      >
        <NearestBookingsHeader
          bookings={nearest}
          orderedResourcesConfig={orderedResourcesConfig}
          hoveredBookingId={hoveredBookingId}
          handleOpenModal={handleOpenModal}
          handleHoverStart={handleHoverStart}
          handleHoverEnd={handleHoverEnd}
        />
      </div>
    ),
    renderLegend: () => null,
    renderBookingCard: (booking, idx, rowHeight) => {
      // Render resource-level blockers per lane
      const isBlocker = booking?.status === 'blocked';
      const rid = booking?.resourceId?._id || booking?.resourceId || null;
      const ridStr = rid ? String(rid) : null;
      const laneOffsetMap = ridStr ? laneOffsetDisplayIndexById.get(ridStr) : null;

      if (isBlocker && ridStr && laneOffsetMap && laneOffsetMap.size > 0) {
        const blockerColor = generateUniqueColor(String(booking._id));
        const durationMs =
          booking.eventDurationMS || new Date(booking.endTime) - new Date(booking.startTime) || 0;
        const requested = resolveBlockedSlotCount(booking);
        const laneIndices = Array.from(laneOffsetMap.values()).slice(
          0,
          Math.min(laneOffsetMap.size, requested)
        );
        const isHighlighted = hoveredBookingId === String(booking._id);

        if (laneIndices.length === 0) {
          return null;
        }

        const firstLaneIndex = Math.min(...laneIndices);
        return (
          <InternalBookingCardBase
            key={`${booking._id}-lane-span-${firstLaneIndex}`}
            booking={{ ...booking, i: firstLaneIndex, eventDurationMS: durationMs }}
            rowSpan={laneIndices.length}
            getParticipantCount={() => 0}
            onCardClick={handleOpenModal}
            onHoverStart={handleHoverStart}
            onHoverEnd={handleHoverEnd}
            isExternallyHighlighted={isHighlighted}
            renderAvailableSlots={false}
            rowHeight={rowHeight}
            slotWidthPx={80}
            minutesPerSlot={stepMinutes}
            showIcons={false}
            showPricingName={false}
            showDuration={false}
            entertainment={entertainment}
            isShiftable={() => false}
            isBookingReadyForShift={() => false}
            color={blockerColor}
          />
        );
      }

      // Entertainment-weite Blocker (ohne resourceId): als Segment in allen Lanes zeigen
      if (isBlocker && !ridStr && laneOffsetDisplayIndexById.size > 0) {
        const blockerColor = generateUniqueColor(String(booking._id));
        const durationMs =
          booking.eventDurationMS || new Date(booking.endTime) - new Date(booking.startTime) || 0;
        const allLaneIndices = Array.from(laneOffsetDisplayIndexById.values()).flatMap(map =>
          Array.from(map.values())
        );
        const isHighlighted = hoveredBookingId === String(booking._id);
        if (allLaneIndices.length === 0) {
          return null;
        }
        const firstLaneIndex = Math.min(...allLaneIndices);
        return (
          <InternalBookingCardBase
            key={`${booking._id}-lane-span-global-${firstLaneIndex}`}
            booking={{ ...booking, i: firstLaneIndex || 1, eventDurationMS: durationMs }}
            rowSpan={allLaneIndices.length}
            getParticipantCount={() => 0}
            onCardClick={handleOpenModal}
            onHoverStart={handleHoverStart}
            onHoverEnd={handleHoverEnd}
            isExternallyHighlighted={isHighlighted}
            renderAvailableSlots={false}
            rowHeight={rowHeight}
            slotWidthPx={80}
            minutesPerSlot={stepMinutes}
            showIcons={false}
            showPricingName={false}
            showDuration={false}
            entertainment={entertainment}
            isShiftable={() => false}
            isBookingReadyForShift={() => false}
            color={blockerColor}
          />
        );
      }

      // Default: ordered-resources multi-segment booking
      return (
        <BookingCardOrderedResources
          key={booking._id}
          booking={booking}
          orderedResourcesConfig={
            orderedResourcesConfig || {
              laneIndexById: new Map(),
              durationByIdMs: new Map(),
              order: [],
            }
          }
          handleOpenModal={handleOpenModal}
          onHoverStart={handleHoverStart}
          onHoverEnd={handleHoverEnd}
          externallyHighlightedBookingId={hoveredBookingId}
          renderAvailableSlots={false}
          rowHeight={rowHeight}
          slotWidthPx={80}
          minutesPerSlot={stepMinutes}
          onBookingShift={onBookingShift}
          entertainment={entertainment}
          onDragStart={bk => {
            setHoveredBookingId(null);
            setActiveDragBookingId(String(bk._id));
            setDragPreviewStart(null);
            return onDragStart?.(bk);
          }}
          onDragActivated={onDragActivated}
          onDragEnd={() => {
            setDragPreviewStart(null);
            setActiveDragBookingId(null);
            onDragEnd?.();
          }}
          onDragPreview={(bk, start) => setDragPreviewStart(start)}
          previewStartTime={activeDragBookingId === String(booking._id) ? dragPreviewStart : null}
          availableShiftTimes={availableShiftTimes}
          isShiftable={isShiftable}
          isBookingReadyForShift={isBookingReadyForShift}
          externallyDraggedBookingId={activeDragBookingId}
          laneOffsetDisplayIndexById={laneOffsetDisplayIndexById}
          segmentStackIndexByKey={segmentStackIndexByKey}
          baseSlotCountById={orderedResourcesConfig?.baseSlotCountById}
        />
      );
    },
    renderAvailableSlots: false,
    // Blockers are drawn as lane spans by renderBookingCard above, not as the
    // shared single-row blocker card.
    gridRendersBlockers: true,
  };

  return (
    <BookingGridBase
      {...baseProps}
      {...(useResponsiveNearestHeader ? { nearestCount: cols } : {})}
    />
  );
}

export default GridOrderedResources;
